import ScoutAvatar from '../../ScoutAvatar'

import { NorthEast as NorthEastIcon, SouthEast as SouthEastIcon } from '@mui/icons-material'

import GraphicsPanel from './GraphicsPanel'

const clamp = (value, min, max) => Math.min(max, Math.max(min, value))

const getBubbleBounds = (itemCount) => {
	const safeCount = Math.max(1, Number(itemCount || 0))
	const density = clamp(1 - Math.max(0, safeCount - 3) * 0.1, 0.46, 1)
	const minSize = Math.round(clamp(48, 66 * density, 66))
	const maxSize = Math.round(clamp(minSize + 32, 124 * density + 8, 124))

	return {
		minSize,
		maxSize,
	}
}

const getBubbleMetrics = (item, items) => {
	const values = items.map((entry) => Number(entry?.value || 0))
	const maxValue = Math.max(...values, 1)
	const { minSize, maxSize } = getBubbleBounds(items.length)
	const currentValue = Number(item?.value || 0)
	const relativeValue = clamp(currentValue / maxValue, 0, 1)
	const scaledWeight = maxValue <= 0 ? 0 : Math.pow(relativeValue, 1.02)
	const size = Math.round(minSize + (maxSize - minSize) * scaledWeight)
	const contentWidth = clamp(size < 68 ? 76 : 68 - Math.max(0, size - 88) / 12, 58, 76)

	return {
		size,
		contentWidth,
	}
}

const formatTick = (value) => {
	return Number(value || 0).toLocaleString('pt-BR', {
		maximumFractionDigits: 0,
	})
}

const Trend = ({ trend }) => {
	if (!trend?.value) {
		return null
	}

	const isUp = trend.direction !== 'down'
	const TrendIcon = isUp ? NorthEastIcon : SouthEastIcon

	return (
		<span className={isUp ? 'scout-graphics-trend scout-graphics-trend--up' : 'scout-graphics-trend scout-graphics-trend--down'}>
			{trend.value}
			<TrendIcon fontSize="inherit" />
		</span>
	)
}

const ProductsBubbleChart = ({ data, empty = false, panelStyle, bodyStyle }) => {
	const items = data?.items || []
	const ticks = data?.ticks || [0, 5, 10, 15]
	const maxValue = Math.max(...ticks, ...items.map((item) => Number(item.value || 0)), 1)

	return (
		<GraphicsPanel
			title="Produtos e Serviços"
			empty={empty || !items.length}
			style={panelStyle}
			bodyStyle={bodyStyle}
			footer={
				!empty && items.length ? (
					<div className="scout-graphics-footer-row">
						<span>Número de citações: <strong>{data.totalMentions}</strong></span>
						<Trend trend={data.trend} />
					</div>
				) : null
			}
		>
			<div className="scout-graphics-chart scout-graphics-chart--products">
				<div className="scout-graphics-chart__axes scout-graphics-chart__axes--numeric">
					{ticks.slice().reverse().map((tick) => (
						<div key={tick} className="scout-graphics-axis-row">
							<span>{formatTick(tick)}</span>
							<div />
						</div>
					))}
				</div>
				<div className="scout-graphics-chart__plot scout-graphics-chart__plot--products">
					<div className="scout-graphics-grid-lines" aria-hidden="true">
						{ticks.slice().reverse().map((tick) => {
							const offset = maxValue > 0 ? 100 - (Number(tick || 0) / maxValue) * 100 : 100

							return <span key={`line-${tick}`} className="scout-graphics-grid-line" style={{ top: `${offset}%` }} />
						})}
					</div>
					{items.map((item, index) => {
						const bubbleMetrics = getBubbleMetrics(item, items)
						const size = bubbleMetrics.size
						const x = item.x ?? ((index + 1) / (items.length + 1)) * 100
						const y = 100 - (Number(item.value || 0) / maxValue) * 100
						const bubbleStyle = {
							left: `${x}%`,
							top: `${Math.max(18, Math.min(88, y))}%`,
							width: `${size}px`,
							height: `${size}px`,
							background: item.color,
							'--bubble-content-width': `${bubbleMetrics.contentWidth}%`,
							zIndex: Math.max(1, Math.round(100 - x)),
						}

						return (
							<div
								key={item.id}
								className="scout-graphics-bubble"
								style={bubbleStyle}
							>
								<div className={`scout-graphics-bubble__content scout-graphics-bubble__content--${item.nameTone || 'regular'}`}>
									<strong>
										{(item.nameLines || [item.name]).map((line, lineIndex) => (
											<span key={`${item.id}-line-${lineIndex}`}>{line}</span>
										))}
									</strong>
									<span>{item.value}</span>
								</div>
								{item.accountBadges?.length ? (
									<div className="scout-graphics-bubble-account-stack scout-graphics-bubble-account-stack--right">
										{item.accountBadges.map((badge) => (
											<div key={badge.id} className="scout-graphics-bubble-account">
												<ScoutAvatar
													entity={badge}
													alt={badge.name}
													className="scout-graphics-bubble-account__avatar"
													imgStyle={{ borderColor: badge.color }}
													fallback={() => (
														<div className="scout-graphics-bubble-account__avatar" style={{ borderColor: badge.color }}>
															{badge.initials}
														</div>
													)}
												/>
												<div className="scout-graphics-bubble-account__badge scout-graphics-bubble-account__badge--overlay">{badge.label}</div>
											</div>
										))}
									</div>
								) : null}
							</div>
						)
					})}
				</div>
			</div>
		</GraphicsPanel>
	)
}

export default ProductsBubbleChart